import { useState, useCallback } from "react";
import {
  Table,
  TextInput,
  Group,
  Pagination,
  Stack,
  Text,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { LoadingTable } from "../LoadingTable";
import { EmptyState } from "../EmptyState";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: number | string;
}

export interface EmptyStateConfig {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  action?: { label: string; onClick?: () => void; href?: string };
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  loading?: boolean;
  emptyState?: EmptyStateConfig;
  // Pagination
  page?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  total?: number;
  // Search
  searchValue?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  onSearch?: () => void;
  // Filters slot
  filters?: React.ReactNode;
  // Row behavior
  onRowClick?: (row: T) => void;
  rowKey: (row: T) => string;
  // Actions column
  actions?: (row: T) => React.ReactNode;
  // Accessibility
  "aria-label"?: string;
}

export function DataTable<T>({
  columns,
  data,
  loading = false,
  emptyState,
  page,
  totalPages,
  onPageChange,
  total,
  searchValue,
  searchPlaceholder = "Search...",
  onSearchChange,
  onSearch,
  filters,
  onRowClick,
  rowKey,
  actions,
  "aria-label": ariaLabel,
}: DataTableProps<T>) {
  const hasSearch = onSearchChange !== undefined;
  const hasPagination = totalPages !== undefined && totalPages > 1 && onPageChange;

  const allColumns = actions
    ? [...columns, { key: "__actions", header: "", render: actions, width: "auto" }]
    : columns;

  const handleRowKeyDown = useCallback(
    (row: T, e: React.KeyboardEvent<HTMLTableRowElement>) => {
      if (onRowClick && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onRowClick(row);
      }
    },
    [onRowClick],
  );

  return (
    <Stack gap="md">
      {(hasSearch || filters) && (
        <Group gap="sm" role="search">
          {hasSearch && (
            <TextInput
              placeholder={searchPlaceholder}
              aria-label="Search table"
              leftSection={<IconSearch size={16} />}
              value={searchValue}
              onChange={(e) => onSearchChange!(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && onSearch?.()}
              style={{ flex: 1, maxWidth: 400 }}
            />
          )}
          {filters}
        </Group>
      )}

      {loading ? (
        <LoadingTable rows={5} columns={allColumns.length} />
      ) : data.length === 0 ? (
        <EmptyState
          title={emptyState?.title ?? "No data found"}
          description={emptyState?.description}
          icon={emptyState?.icon}
          action={emptyState?.action}
        />
      ) : (
        <Table.ScrollContainer minWidth={500}>
          <Table aria-label={ariaLabel}>
            <Table.Thead>
              <Table.Tr>
                {allColumns.map((col) => (
                  <Table.Th key={col.key} w={col.width}>
                    {col.header}
                  </Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((row) => (
                <Table.Tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick ? (e) => handleRowKeyDown(row, e) : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? "button" : undefined}
                  style={onRowClick ? { cursor: "pointer" } : undefined}
                >
                  {allColumns.map((col) => (
                    <Table.Td key={col.key}>{col.render(row)}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {hasPagination && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {total !== undefined ? `${total} total` : `Page ${page} of ${totalPages}`}
          </Text>
          <Pagination
            aria-label="Pagination"
            value={page}
            onChange={onPageChange}
            total={totalPages!}
          />
        </Group>
      )}
    </Stack>
  );
}
